/**
 * WorkflowIntelligencePageV2 — production-grade V2 redesign (G353).
 *
 * Replaces the V1 tab-clone with the canonical V2 inventory pattern
 * shared by Audit Trail, Compliance Center, and Trust Center:
 *
 *   ┌──────────────────────────────────────────────────────────────────┐
 *   │ Scope toggle (All / Lets / ExteLets)   Time window   actions     │
 *   │ Fleet KPI strip — tenant-wide aggregates                         │
 *   ├─────────────────┬────────────────────────────────────────────────┤
 *   │ FacetRail       │ Workflow inventory table                       │
 *   │ archetype       │  ┌────────────────────────────────────────┐    │
 *   │ status          │  │ click any workflow → slideout drill-in │    │
 *   │ source          │  │   sub-tabs: Performance / Hot-spots /  │    │
 *   │ activity        │  │            Soak / Governance           │    │
 *   └─────────────────┴────────────────────────────────────────────────┘
 *
 * Benchmarks and AI Analyst are top-level *modal actions* — they
 * operate across workflows, not on one. Per-workflow drill-down lives
 * inside the slideout (Performance / Hot-spots / Soak / Governance).
 *
 * Backend endpoints reused unchanged from V1.
 * v1 stays as fallback via /workflow-intelligence?legacy=1.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useToast } from '../contexts/ToastContext';
import * as api from '../services/api';
import { deriveWorkflowArchetype } from '../utils/archetype';
import {
  RefreshCw, Activity, AlertTriangle, TrendingUp, Zap, Shield,
  ArrowRight, BarChart3, Brain, Gauge, ExternalLink, X, Download,
  Copy, CheckCircle, Loader2, Search,
  // RC-39 — icon for the new per-run Runs sub-tab
  List as ListIcon,
} from 'lucide-react';
import {
  Button, Pill, Card, MetricCard, Table, Slideout, EmptyState,
  FacetRail,
} from '../components/v2';
// RC-39 — EntityLabel renders the canonical `name (shortid)` pair
// (UI_DISPLAY_CONVENTIONS R1) for the workflow column in the Runs
// sub-tab. R3 fallback (uses the kind word when name is null) is
// handled inside the component.
import EntityLabel from '../components/common/EntityLabel';
/* G393 — Pagination on the workflow inventory table. Without it, 79+
 * workflows render in a single continuous scroll with no count / page
 * controls. Same pattern as TeamPageV2 / AuditTrailPageV2 / PolicyConfigPageV2. */
import { Pagination, usePaginationState } from '../components/common/Pagination';

import WorkflowIntelligencePage from './WorkflowIntelligencePage';

/* ─── Constants ──────────────────────────────────────────────────────── */
const SCOPE_OPTIONS = [
  { key: 'all',      label: 'All workflows', desc: 'Lets + AutoLets + SysLets + ExteLets' },
  { key: 'native',   label: 'Lets',          desc: 'Native (Lets / AutoLets / SysLets)' },
  { key: 'external', label: 'ExteLets',      desc: 'External agents (GEL-protected)' },
];

const WINDOW_OPTIONS = [
  { id: '1h',  label: 'Last 1 hour',   hours: 1   },
  { id: '4h',  label: 'Last 4 hours',  hours: 4   },
  { id: '8h',  label: 'Last 8 hours',  hours: 8   },
  { id: '12h', label: 'Last 12 hours', hours: 12  },
  { id: '24h', label: 'Last 24 hours', hours: 24  },
  { id: '7d',  label: 'Last 7 days',   hours: 168 },
  { id: '30d', label: 'Last 30 days',  hours: 720 },
];

const SUB_TABS = [
  { key: 'performance', label: 'Performance', icon: Gauge },
  // RC-39 — per-run drill-down. Placed second so the natural reading
  // order is "aggregate stats → individual runs → per-node hotspots
  // → trend → governance" — moving from the most summary view to
  // increasingly granular ones.
  { key: 'runs',        label: 'Runs',        icon: ListIcon },
  { key: 'hotspots',    label: 'Hot-spots',   icon: Zap },
  { key: 'soak',        label: 'Soak / Trend', icon: TrendingUp },
  { key: 'governance',  label: 'Protection',  icon: Shield },
];

const ARCHETYPE_OPTIONS = [
  { key: 'Let',     label: 'Let' },
  { key: 'AutoLet', label: 'AutoLet' },
  { key: 'SysLet',  label: 'SysLet' },
  { key: 'ExteLet', label: 'ExteLet' },
];

const STATUS_OPTIONS = [
  { key: 'healthy',  label: 'Healthy' },
  { key: 'degraded', label: 'Degraded' },
  { key: 'critical', label: 'Critical' },
  { key: 'idle',     label: 'Idle' },
];

const ACTIVITY_OPTIONS = [
  { key: 'hot',  label: 'Active in window' },
  { key: 'cold', label: 'No runs in window' },
];

const hoursForWindow = (id) =>
  (WINDOW_OPTIONS.find(w => w.id === id) || WINDOW_OPTIONS.find(w => w.id === '7d')).hours;

const bucketForWindow = (id) => {
  if (id === '1h')                       return '1m';
  if (id === '4h' || id === '8h')        return '5m';
  if (id === '12h' || id === '24h')      return '15m';
  if (id === '7d')                       return '1h';
  return '1d';
};

/* ─── Derivation helpers (consistent across V2 surfaces) ───────────────
 * G388 — Delegate to the shared utility so every surface in the app reads
 * archetype the same way. Pre-G388 there were three parallel implementations
 * that drifted in subtle ways. The new helper reads V177's authoritative
 * `workflow.kind` column when present and falls back to heuristics for
 * pre-V177 rows. (Import lives at the top of the file.) */
function deriveArchetype(w) {
  return deriveWorkflowArchetype(w);
}

/* Status derivation — driven by P99/error/drift if present, else "idle". */
function deriveStatus(w) {
  if (w.status) return String(w.status).toLowerCase();
  const runs = w.totalRunCount ?? w.runCount ?? 0;
  if (runs === 0) return 'idle';
  const er = w.errorRate != null ? (w.errorRate > 1 ? w.errorRate : w.errorRate * 100) : 0;
  const drift = (w.driftSignals || 0) > 0;
  if (er > 5 || drift) return 'critical';
  if (er > 1) return 'degraded';
  return 'healthy';
}

const STATUS_TONE = { healthy: 'success', degraded: 'warning', critical: 'danger', idle: 'muted' };

/* ─── Formatters ─────────────────────────────────────────────────────── */
function fmtMs(n) {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  const v = Number(n);
  if (v < 1) return `${v.toFixed(2)} ms`;
  if (v < 1000) return `${Math.round(v)} ms`;
  return `${(v / 1000).toFixed(2)} s`;
}
function fmtPct(n, places = 1) {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  return `${Number(n).toFixed(places)}%`;
}
function fmtRate(n) {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  const v = Number(n);
  if (v >= 1000) return `${(v / 1000).toFixed(1)}k/s`;
  if (v >= 10)   return `${v.toFixed(0)}/s`;
  return `${v.toFixed(2)}/s`;
}
function fmtTime(t) {
  if (!t) return '—';
  const d = new Date(t);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString();
}

/* ════════════════════════════════════════════════════════════════════════
 *  SHELL
 * ──────────────────────────────────────────────────────────────────────── */
function WorkflowIntelligencePageV2Inner() {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  /* URL-restorable filter + scope state. */
  const [scope, setScope] = useState(() => {
    const s = searchParams.get('scope');
    return SCOPE_OPTIONS.some(o => o.key === s) ? s : 'all';
  });
  const [windowId, setWindowId] = useState(() => {
    const w = searchParams.get('window');
    return WINDOW_OPTIONS.some(o => o.id === w) ? w : '7d';
  });
  const [searchText, setSearchText] = useState(() => searchParams.get('q') || '');
  const [openWorkflowId, setOpenWorkflowId] = useState(() => searchParams.get('workflowId') || null);
  const [openSubTab, setOpenSubTab] = useState(() => {
    const t = searchParams.get('tab');
    return SUB_TABS.some(x => x.key === t) ? t : 'performance';
  });

  /* Facet selections — left rail */
  const [facets, setFacets] = useState(() => {
    const fromParam = (p) => {
      const v = searchParams.get(p);
      return v ? new Set(v.split(',').filter(Boolean)) : new Set();
    };
    return {
      archetype: fromParam('archetype'),
      status:    fromParam('status'),
      activity:  fromParam('activity'),
    };
  });
  const toggleFacet = (gKey, oKey, c) => setFacets(p => {
    const n = new Set(p[gKey] || []);
    c ? n.add(oKey) : n.delete(oKey);
    return { ...p, [gKey]: n };
  });
  const clearFacets = () => setFacets({ archetype: new Set(), status: new Set(), activity: new Set() });

  /* Data — fleet-level. Per-workflow detail loads inside the slideout. */
  const [workflows, setWorkflows] = useState([]);
  const [fleetPerf, setFleetPerf] = useState(null);
  const [benchmarks, setBenchmarks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);
  const [nowMs, setNowMs] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const lastRefreshedLabel = useMemo(() => {
    if (!lastRefreshedAt) return null;
    const dt = Math.max(0, Math.floor((nowMs - lastRefreshedAt) / 1000));
    if (dt < 5)    return 'just now';
    if (dt < 60)   return `${dt}s ago`;
    if (dt < 3600) return `${Math.floor(dt / 60)}m ago`;
    return `${Math.floor(dt / 3600)}h ago`;
  }, [lastRefreshedAt, nowMs]);

  /* G356 — Top-level slideout state declared BEFORE the URL-sync
   * useEffect. The dep array of that effect references fleetOpen +
   * fleetSubTab; with the previous ordering the array literal hit a
   * Temporal Dead Zone (those consts weren't yet initialised when
   * useEffect(...) was called), crashing the page with "Cannot access
   * 'je' before initialization". */
  const [benchmarksOpen, setBenchmarksOpen] = useState(false);
  const [aiAnalystOpen,  setAIAnalystOpen]  = useState(false);
  const [fleetOpen, setFleetOpen] = useState(() => searchParams.get('fleet') === '1');
  const [fleetSubTab, setFleetSubTab] = useState(() => {
    const t = searchParams.get('fleetTab');
    return SUB_TABS.some(x => x.key === t) ? t : 'performance';
  });

  /* URL sync. */
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    const setOrDel = (k, v) => { if (v) next.set(k, v); else next.delete(k); };
    setOrDel('scope',     scope === 'all' ? '' : scope);
    setOrDel('window',    windowId === '7d' ? '' : windowId);
    setOrDel('q',         searchText);
    setOrDel('workflowId', openWorkflowId || '');
    setOrDel('tab',       openWorkflowId && openSubTab !== 'performance' ? openSubTab : '');
    setOrDel('archetype', [...(facets.archetype || [])].join(',') || '');
    setOrDel('status',    [...(facets.status    || [])].join(',') || '');
    setOrDel('activity',  [...(facets.activity  || [])].join(',') || '');
    setOrDel('fleet',     fleetOpen ? '1' : '');
    setOrDel('fleetTab',  fleetOpen && fleetSubTab !== 'performance' ? fleetSubTab : '');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line
  }, [scope, windowId, searchText, openWorkflowId, openSubTab, facets, fleetOpen, fleetSubTab]);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const hours = hoursForWindow(windowId);
      const fromInstant = new Date(Date.now() - hours * 3600 * 1000).toISOString();
      const [wfRes, perfRes, benchRes] = await Promise.all([
        api.getWorkflowIntelligenceWorkflows?.({ from: fromInstant }).catch((e) => { throw e; }),
        api.getWorkflowIntelligencePerformance?.({ from: fromInstant }).catch(() => null),
        api.listBenchmarks?.().catch(() => null),
      ]);
      const wfItems = Array.isArray(wfRes?.data?.items) ? wfRes.data.items
                   : Array.isArray(wfRes?.items) ? wfRes.items
                   : Array.isArray(wfRes?.data) ? wfRes.data
                   : Array.isArray(wfRes) ? wfRes : [];
      setWorkflows(wfItems);
      setFleetPerf(perfRes?.data || perfRes || null);
      const benchItems = Array.isArray(benchRes?.data?.items) ? benchRes.data.items
                       : Array.isArray(benchRes?.items) ? benchRes.items
                       : Array.isArray(benchRes?.data) ? benchRes.data
                       : Array.isArray(benchRes) ? benchRes : [];
      setBenchmarks(benchItems);
      setLastRefreshedAt(Date.now());
    } catch (err) {
      setError(err?.message || 'Failed to load workflow intelligence');
      addToast({ tone: 'danger', title: 'Load failed', description: err?.message });
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, [windowId]);
  useEffect(() => { reload(); }, [reload]);

  /* G356 — Slideout state was previously declared here but caused a
   * Temporal Dead Zone crash because the URL-sync useEffect dep array
   * (further above) referenced fleetOpen/fleetSubTab. Moved upward;
   * leave a marker so future edits don't re-declare here. */

  /* Derive enriched + filtered + scope-aware workflow list. */
  const enriched = useMemo(() => workflows.map(w => ({
    ...w,
    archetype: deriveArchetype(w),
    status:    deriveStatus(w),
    runs:      w.totalRunCount ?? w.runCount ?? 0,
  })), [workflows]);

  const scopedAll = useMemo(() => {
    if (scope === 'native')   return enriched.filter(w => w.archetype !== 'ExteLet');
    if (scope === 'external') return enriched.filter(w => w.archetype === 'ExteLet');
    return enriched;
  }, [enriched, scope]);

  /* Facet matcher. */
  const valueFor = (w, key) =>
      key === 'archetype' ? w.archetype
    : key === 'status'    ? w.status
    : key === 'activity'  ? (w.runs > 0 ? 'hot' : 'cold')
    : w[key];

  const filtered = useMemo(() => scopedAll.filter(w => {
    for (const g of ['archetype', 'status', 'activity']) {
      const sel = facets[g];
      if (sel && sel.size > 0 && !sel.has(valueFor(w, g))) return false;
    }
    if (searchText) {
      const needle = searchText.toLowerCase();
      const hay = [w.name, w.workflowName, w.id, w.description, w.archetype].filter(Boolean).join(' ').toLowerCase();
      if (!hay.includes(needle)) return false;
    }
    return true;
  }), [scopedAll, facets, searchText]);

  /* G393 — Inventory pagination. 25 rows fit comfortably above the fold on
   * a 1440px viewport; bump to 50 if the operator needs density. Page resets
   * to 0 whenever the filtered length changes so the user never lands on an
   * empty page after applying a facet. */
  const pg = usePaginationState(25);
  useEffect(() => { pg.setPage(0); }, [filtered.length]); // eslint-disable-line

  /* Live facet counts (mirror Audit Trail pattern). */
  const facetCounts = useMemo(() => {
    const counts = { archetype: {}, status: {}, activity: {} };
    const groups = [
      { key: 'archetype', options: ARCHETYPE_OPTIONS },
      { key: 'status',    options: STATUS_OPTIONS },
      { key: 'activity',  options: ACTIVITY_OPTIONS },
    ];
    for (const g of groups) {
      for (const opt of g.options) {
        counts[g.key][opt.key] = scopedAll.filter(w => {
          for (const og of groups) {
            if (og.key === g.key) continue;
            const sel = facets[og.key]; if (!sel || sel.size === 0) continue;
            if (!sel.has(valueFor(w, og.key))) return false;
          }
          return valueFor(w, g.key) === opt.key;
        }).length;
      }
    }
    return counts;
  }, [scopedAll, facets]);

  const facetGroups = useMemo(() => [
    { key: 'archetype', label: 'Archetype',
      options: ARCHETYPE_OPTIONS.map(o => ({ ...o, count: facetCounts.archetype[o.key] || 0, checked: facets.archetype?.has(o.key) || false })) },
    { key: 'status',    label: 'Status',
      options: STATUS_OPTIONS.map(o => ({ ...o, count: facetCounts.status[o.key] || 0, checked: facets.status?.has(o.key) || false })) },
    { key: 'activity',  label: 'Activity',
      options: ACTIVITY_OPTIONS.map(o => ({ ...o, count: facetCounts.activity[o.key] || 0, checked: facets.activity?.has(o.key) || false })) },
  ], [facetCounts, facets]);

  /* Fleet KPIs derived from fleet-wide performance + workflow inventory. */
  const fleetKpis = useMemo(() => {
    const total = scopedAll.length;
    const active = scopedAll.filter(w => w.runs > 0).length;
    const critical = scopedAll.filter(w => w.status === 'critical').length;
    const degraded = scopedAll.filter(w => w.status === 'degraded').length;
    const perc = fleetPerf?.percentiles || {};
    return {
      total,
      active,
      critical,
      degraded,
      runs:    fleetPerf?.runCount ?? scopedAll.reduce((s, w) => s + (w.runs || 0), 0),
      p95:     perc.p95 ?? fleetPerf?.latencyP95,
      success: fleetPerf?.successRate != null
        ? (fleetPerf.successRate > 1 ? fleetPerf.successRate : fleetPerf.successRate * 100)
        : null,
    };
  }, [scopedAll, fleetPerf]);

  const activeFilterChips = useMemo(() => {
    const chips = [];
    for (const g of facetGroups) for (const opt of g.options.filter(o => o.checked)) {
      chips.push({ groupKey: g.key, optionKey: opt.key, label: `${g.label}: ${opt.label}` });
    }
    return chips;
  }, [facetGroups]);

  /* ─── Render ─────────────────────────────────────────────────────── */
  const selectedWorkflow = enriched.find(w => w.id === openWorkflowId) || null;

  return (
    <div className="min-h-full bg-v2-page text-v2-ink p-4 sm:p-7 max-w-[1600px] mx-auto">
      {/* Header */}
      <header className="mb-5">
        <div className="flex items-start gap-4 flex-wrap mb-3">
          <div>
            <h1 className="text-[22px] font-bold tracking-tight m-0">Workflow Intelligence</h1>
            <p className="text-[13px] text-v2-ink-secondary mt-0.5">
              {scopedAll.length} workflow{scopedAll.length === 1 ? '' : 's'} in scope · {filtered.length} match filters
              {lastRefreshedLabel && <span className="text-v2-ink-muted"> · refreshed {lastRefreshedLabel}</span>}
            </p>
          </div>
          <div className="ml-auto flex items-center gap-2 flex-wrap justify-end">
            {/* Free-text search */}
            <label className="relative">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-v2-ink-muted" />
              <input type="text" value={searchText} onChange={(e) => setSearchText(e.target.value)}
                placeholder="Search workflows…" aria-label="Search workflows"
                className="pl-7 pr-2.5 py-1.5 text-[12px] border border-v2-border-subtle rounded-v2-sm bg-v2-surface w-[220px] focus:border-v2-action focus:outline-none" />
              {searchText && (
                <button onClick={() => setSearchText('')} aria-label="Clear search"
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 text-v2-ink-muted hover:text-v2-ink p-0.5">
                  <X size={11} />
                </button>
              )}
            </label>
            {/* Time window */}
            <div className="flex items-center gap-1 bg-v2-muted rounded-v2-pill p-1 flex-wrap">
              {WINDOW_OPTIONS.map(t => (
                <button key={t.id} onClick={() => setWindowId(t.id)}
                  className={'px-2.5 py-1 rounded-v2-pill text-[11px] font-semibold transition-colors duration-v2 ' +
                    (windowId === t.id ? 'bg-v2-surface text-v2-ink shadow-v2-sm' : 'text-v2-ink-muted hover:text-v2-ink')}>
                  {t.label.replace('Last ', '')}
                </button>
              ))}
            </div>
            <Button variant="ghost" size="sm" iconLeft={<RefreshCw size={13} className={loading ? 'animate-spin' : ''} />} onClick={reload} disabled={loading}>Refresh</Button>
            {/* G356 — Fleet drill-in. Opens the same Performance / Hot-spots /
                Soak / Governance sub-tab shape as the per-workflow slideout,
                but scoped to the entire fleet for the current time window. */}
            <Button variant="primary" size="sm" iconLeft={<Activity size={13} />}
              onClick={() => { setFleetOpen(true); setFleetSubTab('performance'); }}>
              Fleet metrics
            </Button>
            <Button variant="secondary" size="sm" iconLeft={<BarChart3 size={13} />} onClick={() => setBenchmarksOpen(true)}>Benchmarks</Button>
            <Button variant="secondary" size="sm" iconLeft={<Brain size={13} />} onClick={() => setAIAnalystOpen(true)}>AI Analyst</Button>
          </div>
        </div>

        {/* Scope segmented control */}
        <div className="flex items-center gap-1 bg-v2-muted rounded-v2-pill p-1 inline-flex" role="tablist" aria-label="Workflow scope">
          {SCOPE_OPTIONS.map(s => (
            <button key={s.key} onClick={() => setScope(s.key)}
              role="tab" aria-selected={scope === s.key}
              title={s.desc}
              className={'px-3.5 py-1.5 rounded-v2-pill text-[12px] font-semibold transition-colors duration-v2 ' +
                (scope === s.key ? 'bg-v2-surface text-v2-ink shadow-v2-sm' : 'text-v2-ink-muted hover:text-v2-ink')}>
              {s.label}
            </button>
          ))}
        </div>
      </header>

      {/* Fleet KPI strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3 mb-5">
        <MetricCard label="Workflows"   value={fleetKpis.total.toLocaleString()} tone="cyan" />
        <MetricCard label="Active"      value={fleetKpis.active.toLocaleString()} tone="action" />
        <MetricCard label="Critical"    value={fleetKpis.critical.toLocaleString()}
          tone={fleetKpis.critical > 0 ? 'danger' : 'success'} />
        <MetricCard label="Degraded"    value={fleetKpis.degraded.toLocaleString()}
          tone={fleetKpis.degraded > 0 ? 'warning' : 'success'} />
        <MetricCard label="Runs"        value={fleetKpis.runs?.toLocaleString() || '—'} tone="trust" />
        <MetricCard label="Fleet P95"   value={fmtMs(fleetKpis.p95)}
          tone={fleetKpis.p95 > 5000 ? 'warning' : 'success'} />
      </div>

      {/* Error banner */}
      {error && (
        <div className="rounded-v2-md border-l-4 border-v2-danger bg-v2-danger-soft px-4 py-3 mb-5 flex items-center gap-3">
          <AlertTriangle size={18} className="text-v2-danger" />
          <div className="flex-1 text-[12.5px]">
            <div className="font-bold text-v2-ink">Load failed</div>
            <div className="text-v2-ink-secondary">{error}</div>
          </div>
          <Button variant="ghost" size="sm" onClick={reload}>Retry</Button>
          <Button variant="ghost" size="sm" iconLeft={<X size={12} />} onClick={() => setError(null)}>Dismiss</Button>
        </div>
      )}

      {/* Active filter chips */}
      {activeFilterChips.length > 0 && (
        <div className="bg-v2-surface border border-v2-border-subtle rounded-v2-md shadow-v2-sm px-4 py-2.5 mb-4 flex items-center gap-2 flex-wrap">
          <span className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold">Filters</span>
          {activeFilterChips.map((c) => (
            <button key={`${c.groupKey}:${c.optionKey}`} onClick={() => toggleFacet(c.groupKey, c.optionKey, false)}
              className="inline-flex items-center gap-1 text-[11.5px] font-medium bg-v2-action-soft text-v2-action-strong rounded-v2-pill px-2.5 py-0.5 hover:bg-v2-action hover:text-white">
              {c.label} ×
            </button>
          ))}
          <button onClick={clearFacets} className="ml-auto text-[11.5px] text-v2-ink-muted hover:text-v2-ink">Clear all</button>
        </div>
      )}

      {/* FacetRail + Inventory */}
      <div className="grid gap-5 grid-cols-1 lg:grid-cols-[240px_1fr]">
        <FacetRail groups={facetGroups} onToggle={toggleFacet} ariaLabel="Workflow filters" />

        {filtered.length === 0 && !loading ? (
          <EmptyState
            icon={<Activity size={20} />}
            title={scopedAll.length === 0 ? 'No workflows in scope' : 'No workflows match filters'}
            description={scopedAll.length === 0
              ? 'Try a wider time window or switch scope to "All workflows".'
              : 'Adjust facets or clear them to widen the result set.'}
            primaryAction={scopedAll.length === 0 ? null
              : <Button variant="primary" onClick={clearFacets}>Clear filters</Button>}
          />
        ) : (
          <Card padding="none">
            <Table
              ariaLabel="Workflow inventory"
              density="compact"
              columns={[
                { key: 'name', header: 'Workflow', render: (w) => (
                  <div>
                    <div className="font-semibold text-[12.5px] text-v2-ink">{w.name || w.workflowName || '—'}</div>
                    <div className="text-[10.5px] text-v2-ink-muted font-mono-v2">{w.id}</div>
                  </div>
                )},
                { key: 'archetype', header: 'Archetype', render: (w) => (
                  <Pill tone={w.archetype === 'ExteLet' ? 'warning' : 'action'} size="sm">{w.archetype}</Pill>
                )},
                { key: 'status', header: 'Status', render: (w) => (
                  <Pill tone={STATUS_TONE[w.status] || 'muted'} size="sm" dot>{w.status}</Pill>
                )},
                { key: 'runs', header: 'Runs', align: 'right', render: (w) => (
                  <span className="tabular-nums text-[12px]">{(w.runs || 0).toLocaleString()}</span>
                )},
                { key: 'errorRate', header: 'Errors', align: 'right', render: (w) => {
                  if (w.errorRate == null) return <span className="text-v2-ink-muted text-[11px]">—</span>;
                  const e = w.errorRate > 1 ? w.errorRate : w.errorRate * 100;
                  return <span className={'tabular-nums text-[11.5px] ' + (e > 5 ? 'text-v2-danger font-semibold' : 'text-v2-ink-muted')}>
                    {e.toFixed(1)}%
                  </span>;
                }},
                { key: 'drift', header: 'Drift', render: (w) => (
                  (w.driftSignals || 0) > 0
                    ? <Pill tone="warning" size="sm" dot>{w.driftSignals}</Pill>
                    : <span className="text-v2-ink-muted text-[11px]">·</span>
                )},
                { key: 'open', header: '', align: 'right', render: (_w) => (
                  <ArrowRight size={13} className="text-v2-ink-muted" />
                )},
              ]}
              rows={pg.paginate(filtered)}
              rowKey={(w) => w.id}
              onRowClick={(w) => { setOpenWorkflowId(w.id); setOpenSubTab('performance'); }}
            />
            {/* G393 — Footer pagination. Only renders when there's more
                than one page to keep small inventories visually clean. */}
            {filtered.length > pg.pageSize && (
              <div className="border-t border-v2-border-subtle px-3 py-2">
                <Pagination
                  totalItems={filtered.length}
                  page={pg.page}
                  pageSize={pg.pageSize}
                  onPageChange={pg.setPage}
                  onPageSizeChange={pg.setPageSize}
                  noun="workflows"
                />
              </div>
            )}
          </Card>
        )}
      </div>

      {/* Workflow drill-in slideout */}
      <WorkflowDrillinSlideout
        workflow={selectedWorkflow}
        subTab={openSubTab}
        onSubTab={setOpenSubTab}
        windowId={windowId}
        onClose={() => setOpenWorkflowId(null)}
        onOpenAuditRun={(runId, workflowIdOverride) => {
          // RC-40 — Pass runId via a DEDICATED url param, not the free-text
          // `q` search. Pre-fix the link sent `q=<runId>` which AuditTrailPageV2
          // fed into its in-memory haystack search across policy_audit_events
          // (which the substring may not even appear in). With ?runId=<X> the
          // audit page now ALSO loads /api/v1/workflows/runs/{runId}/audit
          // (workflow lifecycle events) so the operator sees the events for
          // that specific run instead of an empty page.
          const params = new URLSearchParams();
          const wfId = workflowIdOverride || selectedWorkflow?.id;
          if (wfId)  params.set('workflowId', wfId);
          if (runId) params.set('runId', runId);
          navigate(`/workflow-audit?${params.toString()}`);
        }}
      />

      {/* G356 — Fleet drill-in slideout. Same shape as per-workflow but
          unscoped — Performance / Hot-spots / Soak / Governance views of
          the entire fleet for the current time window. */}
      <FleetDrillinSlideout
        isOpen={fleetOpen}
        onClose={() => setFleetOpen(false)}
        subTab={fleetSubTab}
        onSubTab={setFleetSubTab}
        windowId={windowId}
        scope={scope}
        onOpenAuditRun={(runId, workflowIdOverride) => {
          // RC-40 — see comment in WorkflowDrillinSlideout above. Fleet mode
          // has no implicit workflow, so we depend on the caller to provide
          // workflowId per-row (PerformanceSubTab carries it on each
          // slowestRuns entry).
          const params = new URLSearchParams();
          if (workflowIdOverride) params.set('workflowId', workflowIdOverride);
          if (runId)              params.set('runId', runId);
          navigate(`/workflow-audit?${params.toString()}`);
        }}
      />

      {/* G356 — Benchmarks slideout (was Modal). */}
      <BenchmarksSlideout
        isOpen={benchmarksOpen}
        onClose={() => setBenchmarksOpen(false)}
        benchmarks={benchmarks}
      />

      {/* G356 — AI Analyst slideout (was Modal). Now supports a Run-level
          scope when a workflow is selected. */}
      <AIAnalystSlideout
        isOpen={aiAnalystOpen}
        onClose={() => setAIAnalystOpen(false)}
        workflows={enriched}
        benchmarks={benchmarks}
        defaultWorkflowId={openWorkflowId}
        windowId={windowId}
      />
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════
 *  FLEET DRILL-IN SLIDEOUT (G356)
 *
 *  Mirrors WorkflowDrillinSlideout but fetches with workflowId=null so
 *  the backend returns fleet-wide aggregates. Renders the same four
 *  sub-tab components, which already handle the no-workflow case via
 *  their existing empty-state fallbacks.
 * ──────────────────────────────────────────────────────────────────────── */
function FleetDrillinSlideout({ isOpen, onClose, subTab, onSubTab, windowId, scope, onOpenAuditRun }) {
  const [perfData, setPerfData]       = useState(null);
  const [hotspotData, setHotspotData] = useState(null);
  const [seriesData, setSeriesData]   = useState(null);
  const [govData, setGovData]         = useState(null);
  const [subLoading, setSubLoading]   = useState(false);

  const fromInstant = useMemo(() => {
    const hours = hoursForWindow(windowId);
    return new Date(Date.now() - hours * 3600 * 1000).toISOString();
  }, [windowId]);

  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    setSubLoading(true);
    const loader =
      subTab === 'performance' ? () => api.getWorkflowIntelligencePerformance?.({ from: fromInstant }).then(r => !cancelled && setPerfData(r?.data || r || null))
      : subTab === 'hotspots'   ? () => api.getWorkflowIntelligenceNodeHotspots?.({ from: fromInstant }).then(r => !cancelled && setHotspotData(r?.data || r || null))
      : subTab === 'soak'       ? () => api.getWorkflowIntelligenceTimeSeries?.({ from: fromInstant, bucketSize: bucketForWindow(windowId) }).then(r => !cancelled && setSeriesData(r?.data || r || null))
      : subTab === 'governance' ? () => api.getWorkflowIntelligenceGovernance?.({ from: fromInstant }).then(r => !cancelled && setGovData(r?.data || r || null))
      : null;
    if (loader) {
      Promise.resolve(loader()).catch(() => {}).finally(() => { if (!cancelled) setSubLoading(false); });
    } else { setSubLoading(false); }
    return () => { cancelled = true; };
  }, [isOpen, subTab, fromInstant, windowId]);

  if (!isOpen) return null;
  const windowLabel = WINDOW_OPTIONS.find(w => w.id === windowId)?.label || '';
  const scopeLabel = SCOPE_OPTIONS.find(s => s.key === scope)?.label || 'All workflows';

  return (
    <Slideout
      isOpen={isOpen}
      onClose={onClose}
      width={900}
      ariaLabel="Fleet metrics drill-in"
      header={
        <div>
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <Activity size={14} className="text-v2-action-strong" />
            <Pill tone="action" size="sm">Fleet aggregate</Pill>
            <Pill tone="muted" size="sm">{scopeLabel}</Pill>
            <span className="text-[11px] text-v2-ink-muted">· {windowLabel.toLowerCase()}</span>
          </div>
          <h2 className="text-[16px] font-bold m-0">Fleet metrics — {scopeLabel}</h2>
          <p className="text-[11.5px] text-v2-ink-muted mt-0.5">Aggregate stats across every workflow in the current scope.</p>

          <nav className="flex items-stretch gap-0.5 border-b border-v2-border-subtle overflow-x-auto mt-3" role="tablist">
            {SUB_TABS.map(t => {
              const Icon = t.icon;
              const active = subTab === t.key;
              return (
                <button key={t.key}
                  role="tab" aria-selected={active}
                  onClick={() => onSubTab(t.key)}
                  className={'flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-semibold border-b-2 -mb-px whitespace-nowrap transition-colors ' +
                    (active
                      ? 'border-v2-action text-v2-action-strong'
                      : 'border-transparent text-v2-ink-muted hover:text-v2-ink hover:border-v2-border')}>
                  <Icon size={12} />
                  {t.label}
                </button>
              );
            })}
          </nav>
        </div>
      }
    >
      {subLoading && (
        <div className="flex items-center justify-center gap-2 py-6 text-[12.5px] text-v2-ink-secondary">
          <Loader2 size={14} className="animate-spin text-v2-action-strong" />
          Loading fleet {subTab}…
        </div>
      )}
      {!subLoading && subTab === 'performance' && (
        <PerformanceSubTab data={perfData} windowHours={hoursForWindow(windowId)} onOpenAuditRun={onOpenAuditRun} />
      )}
      {/* RC-39 — Runs sub-tab self-fetches because its filter+sort+page
          state is dynamic; bypasses the parent loader switch above so
          we don't have to plumb every filter change through the parent.
          Always rendered as a fresh mount when subTab='runs' so a tab
          switch resets the page to 1, which matches operator
          expectations (consistent with Audit Trail / Team pages). */}
      {subTab === 'runs' && (
        <RunsSubTab workflowId={null} fromInstant={fromInstant} windowLabel={windowLabel} />
      )}
      {!subLoading && subTab === 'hotspots' && (
        <HotspotsSubTab data={hotspotData} />
      )}
      {!subLoading && subTab === 'soak' && (
        <SoakSubTab data={seriesData} bucketSize={bucketForWindow(windowId)} windowLabel={windowLabel} />
      )}
      {!subLoading && subTab === 'governance' && (
        <GovernanceSubTab data={govData} />
      )}
    </Slideout>
  );
}

/* ════════════════════════════════════════════════════════════════════════
 *  WORKFLOW DRILL-IN SLIDEOUT
 *  Sub-tabs: Performance / Hot-spots / Soak / Governance
 *  All scoped to ONE workflow.
 * ──────────────────────────────────────────────────────────────────────── */
function WorkflowDrillinSlideout({ workflow, subTab, onSubTab, windowId, onClose, onOpenAuditRun }) {
  const [perfData, setPerfData]       = useState(null);
  const [hotspotData, setHotspotData] = useState(null);
  const [seriesData, setSeriesData]   = useState(null);
  const [govData, setGovData]         = useState(null);
  const [subLoading, setSubLoading]   = useState(false);

  const fromInstant = useMemo(() => {
    const hours = hoursForWindow(windowId);
    return new Date(Date.now() - hours * 3600 * 1000).toISOString();
  }, [windowId]);

  /* Load per-sub-tab on activation. */
  useEffect(() => {
    if (!workflow?.id) return;
    let cancelled = false;
    setSubLoading(true);
    const loader =
      subTab === 'performance' ? () => api.getWorkflowIntelligencePerformance?.({ from: fromInstant, workflowId: workflow.id }).then(r => !cancelled && setPerfData(r?.data || r || null))
      : subTab === 'hotspots'   ? () => api.getWorkflowIntelligenceNodeHotspots?.({ from: fromInstant, workflowId: workflow.id }).then(r => !cancelled && setHotspotData(r?.data || r || null))
      : subTab === 'soak'       ? () => api.getWorkflowIntelligenceTimeSeries?.({ from: fromInstant, workflowId: workflow.id, bucketSize: bucketForWindow(windowId) }).then(r => !cancelled && setSeriesData(r?.data || r || null))
      : subTab === 'governance' ? () => api.getWorkflowIntelligenceGovernance?.({ from: fromInstant, workflowId: workflow.id }).then(r => !cancelled && setGovData(r?.data || r || null))
      : null;
    if (loader) {
      Promise.resolve(loader()).catch(() => {}).finally(() => { if (!cancelled) setSubLoading(false); });
    } else { setSubLoading(false); }
    return () => { cancelled = true; };
  }, [workflow?.id, subTab, fromInstant, windowId]);

  if (!workflow) return null;

  const windowHours = hoursForWindow(windowId);
  const windowLabel = WINDOW_OPTIONS.find(w => w.id === windowId)?.label || '';

  return (
    <Slideout
      isOpen={!!workflow}
      onClose={onClose}
      width={900}
      ariaLabel={`Workflow ${workflow.name || workflow.id} drill-in`}
      header={
        <div>
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <Pill tone={workflow.archetype === 'ExteLet' ? 'warning' : 'action'} size="sm">{workflow.archetype}</Pill>
            <Pill tone={STATUS_TONE[workflow.status] || 'muted'} size="sm" dot>{workflow.status}</Pill>
            <span className="text-[11px] text-v2-ink-muted">· {windowLabel.toLowerCase()}</span>
          </div>
          <h2 className="text-[16px] font-bold m-0">{workflow.name || workflow.workflowName || workflow.id}</h2>
          <p className="text-[11px] text-v2-ink-muted font-mono-v2 mt-0.5">{workflow.id}</p>

          {/* Sub-tab nav */}
          <nav className="flex items-stretch gap-0.5 border-b border-v2-border-subtle overflow-x-auto mt-3" role="tablist">
            {SUB_TABS.map(t => {
              const Icon = t.icon;
              const active = subTab === t.key;
              return (
                <button key={t.key}
                  role="tab" aria-selected={active}
                  onClick={() => onSubTab(t.key)}
                  className={'flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-semibold border-b-2 -mb-px whitespace-nowrap transition-colors ' +
                    (active
                      ? 'border-v2-action text-v2-action-strong'
                      : 'border-transparent text-v2-ink-muted hover:text-v2-ink hover:border-v2-border')}>
                  <Icon size={12} />
                  {t.label}
                </button>
              );
            })}
          </nav>
        </div>
      }
    >
      {subLoading && (
        <div className="flex items-center justify-center gap-2 py-6 text-[12.5px] text-v2-ink-secondary">
          <Loader2 size={14} className="animate-spin text-v2-action-strong" />
          Loading {subTab}…
        </div>
      )}

      {!subLoading && subTab === 'performance' && (
        <PerformanceSubTab data={perfData} windowHours={windowHours} onOpenAuditRun={onOpenAuditRun} />
      )}
      {/* RC-39 — Runs sub-tab scoped to this one workflow. Same component
          as the fleet view; the workflowId prop is the only difference. */}
      {subTab === 'runs' && (
        <RunsSubTab workflowId={workflow?.id || null} fromInstant={fromInstant} windowLabel={windowLabel} />
      )}
      {!subLoading && subTab === 'hotspots' && (
        <HotspotsSubTab data={hotspotData} />
      )}
      {!subLoading && subTab === 'soak' && (
        <SoakSubTab data={seriesData} bucketSize={bucketForWindow(windowId)} windowLabel={windowLabel} />
      )}
      {!subLoading && subTab === 'governance' && (
        <GovernanceSubTab data={govData} />
      )}
    </Slideout>
  );
}

/* ════════════════════════════════════════════════════════════════════════
 *  SUB-TABS — scoped to one workflow
 * ──────────────────────────────────────────────────────────────────────── */
function PerformanceSubTab({ data, windowHours, onOpenAuditRun }) {
  if (!data) return <EmptyState icon={<Gauge size={20} />} title="No performance data"
    description="This workflow returned no runs in the selected window." />;
  const perc = data.percentiles || {};
  // RC-32 (a) — bug fix: BE serves task-grain percentiles under
  // `taskMetrics.taskDurationMs.{p50,p90,p95,p99,max}`, NOT under
  // `taskMetrics.percentiles`. Previous typo here left the P99 TASK tile
  // permanently empty and broke the per-task PercentileBars row.
  const taskPerc = data.taskMetrics?.taskDurationMs || {};
  const tm = data.taskMetrics || {};
  const tpw = tm.tasksPerWorkflow || {};
  const runs = data.runCount ?? data.totalRuns ?? data.runs ?? 0;
  const successRate = data.successRate != null
    ? (data.successRate > 1 ? data.successRate : data.successRate * 100) : null;
  const errorRate = data.errorRate != null
    ? (data.errorRate > 1 ? data.errorRate : data.errorRate * 100) : null;
  const wps = data.workflowsPerSec ?? (runs > 0 ? runs / Math.max(windowHours * 3600, 1) : null);
  // RC-32 (b) — sustained / peak burst, served by BE alongside the
  // window-averaged rate. Window-averaged answers "how busy on average";
  // sustained answers "what does the platform hold under load."
  const wpsPeakSustained = data.peakSustainedWorkflowsPerSec ?? null;
  const tasksPerSec = tm.tasksPerSec ?? null;
  const tasksPerSecPeak = tm.peakSustainedTasksPerSec ?? null;
  const burstCeiling = tm.p99TaskBurstPerSec ?? null;
  const effThroughputP99 = data.p99EffectiveThroughputPerSec ?? null;
  const inFlight = data.inFlightCount ?? null;
  const tput = data.throughputPerHour ?? (runs > 0 ? runs / Math.max(windowHours, 1) : null);
  const distribution = Array.isArray(data.distribution) ? data.distribution : [];
  const slowestRuns = Array.isArray(data.slowestRuns) || Array.isArray(data.topSlowRuns)
    ? (data.slowestRuns || data.topSlowRuns) : [];

  return (
    <div className="space-y-4">
      {/* ── Workflow-grain row ─────────────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <MetricCard label="Runs"       value={runs.toLocaleString()}
          delta={inFlight != null && inFlight > 0 ? { value: `${inFlight} in-flight`, intent: 'neutral' } : undefined}
          tone="trust" />
        <MetricCard label="Workflows/sec" value={fmtRate(wps)}
          delta={wpsPeakSustained != null ? { value: `peak ${fmtRate(wpsPeakSustained)}`, baseline: 'sustained', intent: 'neutral' } : undefined}
          tone="action" />
        <MetricCard label="Throughput/hr" value={tput != null ? Math.round(tput).toLocaleString() : '—'} tone="cyan" />
        <MetricCard label="Success"    value={fmtPct(successRate)}
          delta={errorRate != null && errorRate > 0 ? { value: `${errorRate.toFixed(1)}% error`, intent: errorRate > 5 ? 'negative' : 'neutral' } : undefined}
          tone={successRate == null ? 'muted' : successRate >= 99 ? 'success' : successRate >= 95 ? 'warning' : 'danger'} />
        <MetricCard label="Workflow P50" value={fmtMs(perc.p50)} tone="success" />
        <MetricCard label="Workflow P95" value={fmtMs(perc.p95)} tone={(perc.p95 || 0) > 5000 ? 'warning' : 'success'} />
        <MetricCard label="Workflow P99" value={fmtMs(perc.p99)} tone={(perc.p99 || 0) > 10000 ? 'danger' : (perc.p99 || 0) > 5000 ? 'warning' : 'success'} />
        <MetricCard label="Workflow max" value={fmtMs(perc.max)} tone="muted" />
      </div>

      {/* ── Task-grain row (RC-32 b — restores V1 signal V2 dropped) ── */}
      {(tm.totalInvocations != null || tasksPerSec != null) && (
        <div>
          <div className="text-[10.5px] uppercase tracking-wide text-v2-ink-muted font-semibold mb-1">
            Tasks
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <MetricCard label="Total tasks" value={(tm.totalInvocations ?? 0).toLocaleString()}
              delta={tm.uniqueNodeIds != null ? { value: `${tm.uniqueNodeIds} unique nodes`, intent: 'neutral' } : undefined}
              tone="trust" />
            <MetricCard label="Tasks/sec" value={fmtRate(tasksPerSec)}
              delta={tasksPerSecPeak != null ? { value: `peak ${fmtRate(tasksPerSecPeak)}`, baseline: 'sustained', intent: 'neutral' } : undefined}
              tone="action" />
            <MetricCard label="Burst ceiling" value={burstCeiling != null ? `${burstCeiling}/s` : '—'}
              delta={{ value: 'p99 in 1s slice', intent: 'neutral' }}
              tone="cyan" />
            <MetricCard label="Avg tasks/workflow"
              value={tpw.avg != null ? tpw.avg.toFixed(1) : '—'}
              delta={tpw.p99 != null ? { value: `p99 ${tpw.p99} · max ${tpw.max ?? '—'}`, intent: 'neutral' } : undefined}
              tone="muted" />
            <MetricCard label="Task P50"   value={fmtMs(taskPerc.p50)} tone="success" />
            <MetricCard label="Task P95"   value={fmtMs(taskPerc.p95)} tone={(taskPerc.p95 || 0) > 1000 ? 'warning' : 'success'} />
            <MetricCard label="Task P99"   value={fmtMs(taskPerc.p99)} tone={(taskPerc.p99 || 0) > 3000 ? 'warning' : 'success'} />
            <MetricCard label="Effective throughput @ P99"
              value={effThroughputP99 != null ? `${effThroughputP99.toFixed(2)}/s` : '—'}
              delta={{ value: 'completions / p99 latency', intent: 'neutral' }}
              tone="muted" />
          </div>
        </div>
      )}

      <Card padding="sm">
        <Card.Header title="Latency distribution" subtitle={distribution.length === 0 ? 'No data' : `${distribution.length} buckets`} />
        <Card.Body>
          {distribution.length === 0
            ? <p className="text-[12.5px] text-v2-ink-muted m-0 italic">Distribution not supplied.</p>
            : <Histogram buckets={distribution} />}
        </Card.Body>
      </Card>

      <Card padding="sm">
        <Card.Header title="Percentile breakdown" />
        <Card.Body>
          <PercentileBars
            workflow={{ p50: perc.p50, p90: perc.p90, p95: perc.p95, p99: perc.p99, max: perc.max }}
            task={{ p50: taskPerc.p50, p90: taskPerc.p90, p95: taskPerc.p95, p99: taskPerc.p99, max: taskPerc.max }}
          />
        </Card.Body>
      </Card>

      <Card padding="sm">
        <Card.Header title={`Slowest runs · ${slowestRuns.length}`} subtitle="Click to open in Audit Trail." />
        <Card.Body>
          {slowestRuns.length === 0
            ? <p className="text-[12.5px] text-v2-ink-muted m-0 italic">No slow-run records returned.</p>
            : (
              <Table
                ariaLabel="Slowest runs"
                density="compact"
                columns={[
                  { key: 'run', header: 'Run', render: (r) => (
                    <div className="font-mono-v2 text-[10.5px] text-v2-ink-secondary">{String(r.runId || r.id || '').slice(0, 14)}…</div>
                  )},
                  { key: 'started', header: 'Started', render: (r) => (
                    <span className="text-[11px] text-v2-ink-muted">{fmtTime(r.startedAt || r.timestamp)}</span>
                  )},
                  { key: 'duration', header: 'Duration', align: 'right', render: (r) => (
                    <span className="font-mono-v2 text-[11.5px] tabular-nums">{fmtMs(r.durationMs ?? r.duration ?? r.elapsedMs)}</span>
                  )},
                  { key: 'status', header: 'Status', render: (r) => {
                    const s = (r.status || r.outcome || '').toUpperCase();
                    return s === 'FAILED' || s === 'ERROR'
                      ? <Pill tone="danger" size="sm">{s}</Pill>
                      : <Pill tone="success" size="sm">{s || 'OK'}</Pill>;
                  }},
                  { key: 'open', header: '', align: 'right', render: (r) => (
                    <Button variant="ghost" size="xs" iconRight={<ExternalLink size={11} />}
                      // RC-40 — pass workflowId too so the audit page can
                      // load workflow-scoped lifecycle events alongside
                      // policy events. Stress workflows etc. don't emit
                      // policy events, so without the workflow lookup the
                      // landing page is empty.
                      onClick={() => onOpenAuditRun(r.runId || r.id, r.workflowId)}>
                      Audit
                    </Button>
                  )},
                ]}
                rows={slowestRuns.slice(0, 20)}
                rowKey={(r, i) => r.runId || r.id || i}
              />
            )}
        </Card.Body>
      </Card>
    </div>
  );
}

function HotspotsSubTab({ data }) {
  const nodes = Array.isArray(data?.hotspots) ? data.hotspots
              : Array.isArray(data?.items) ? data.items
              : Array.isArray(data?.nodes) ? data.nodes
              : Array.isArray(data) ? data : [];
  if (nodes.length === 0) return <EmptyState icon={<Zap size={20} />} title="No hot-spots"
    description="This workflow has no node-level latency outliers in the window." />;
  const slowest = nodes.slice().sort((a, b) => (b.p99 ?? b.latencyP99 ?? 0) - (a.p99 ?? a.latencyP99 ?? 0))[0];

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        <MetricCard label="Distinct nodes" value={nodes.length} tone="cyan" />
        <MetricCard label="Slowest P99" value={fmtMs(slowest?.p99 ?? slowest?.latencyP99)} tone="danger" />
        <MetricCard label="Drift detected" value={nodes.filter(n => n.driftDetected).length} tone="warning" />
      </div>
      <Card padding="sm">
        <Card.Header title={`Hot-node ranking · ${nodes.length}`} />
        <Card.Body>
          <Table
            ariaLabel="Hot nodes"
            density="compact"
            columns={[
              { key: 'rank', header: '#', render: (_r, i) => (
                <span className="font-mono-v2 text-[11px] text-v2-ink-muted tabular-nums">{i + 1}</span>
              )},
              { key: 'name', header: 'Node', render: (r) => (
                <div>
                  <div className="font-semibold text-[12px] text-v2-ink">{r.nodeName || r.name || r.taskName}</div>
                  <div className="text-[10px] text-v2-ink-muted font-mono-v2">{r.nodeId || r.id || '—'}</div>
                </div>
              )},
              { key: 'p99', header: 'P99', align: 'right', render: (r) => {
                const v = r.p99 ?? r.latencyP99;
                const max = slowest?.p99 ?? slowest?.latencyP99 ?? 1;
                const pct = v ? Math.min(100, (v / max) * 100) : 0;
                return (
                  <div className="inline-flex items-center gap-2 min-w-[110px] justify-end">
                    <div className="h-1.5 bg-v2-muted rounded-v2-pill overflow-hidden w-12">
                      <div className="h-full bg-v2-danger" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="font-mono-v2 text-[11.5px] tabular-nums w-14 text-right">{fmtMs(v)}</span>
                  </div>
                );
              }},
              { key: 'runs', header: 'Runs', align: 'right', render: (r) => (
                <span className="tabular-nums text-[11.5px]">{(r.runs || r.count || 0).toLocaleString()}</span>
              )},
              { key: 'errors', header: 'Err', align: 'right', render: (r) => {
                const e = r.errorRate != null ? (r.errorRate > 1 ? r.errorRate : r.errorRate * 100) : null;
                return <span className={'tabular-nums text-[11px] ' + (e > 5 ? 'text-v2-danger font-semibold' : 'text-v2-ink-muted')}>
                  {e == null ? '—' : `${e.toFixed(1)}%`}
                </span>;
              }},
              { key: 'drift', header: 'Drift', render: (r) => (
                r.driftDetected
                  ? <Pill tone="warning" size="sm" dot>drift</Pill>
                  : <span className="text-v2-ink-muted text-[11px]">·</span>
              )},
            ]}
            rows={nodes}
            rowKey={(r) => r.nodeId || r.id || (r.nodeName + (r.workflowName || ''))}
          />
        </Card.Body>
      </Card>
    </div>
  );
}

function SoakSubTab({ data, bucketSize, windowLabel }) {
  const buckets = Array.isArray(data?.buckets) ? data.buckets
                : Array.isArray(data?.points) ? data.points
                : Array.isArray(data) ? data : [];
  if (buckets.length === 0) return <EmptyState icon={<TrendingUp size={20} />} title="No time-series"
    description={`No bucketed data for ${windowLabel}.`} />;
  const totals = buckets.reduce((acc, b) => ({
    dispatched: acc.dispatched + Number(b.dispatched ?? b.workflowsDispatched ?? 0),
    completed:  acc.completed  + Number(b.completed ?? b.workflowsCompleted ?? 0),
    errors:     acc.errors     + Number(b.errors ?? b.failedCount ?? 0),
  }), { dispatched: 0, completed: 0, errors: 0 });
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <MetricCard label="Buckets"    value={buckets.length} tone="cyan" />
        <MetricCard label="Dispatched" value={totals.dispatched.toLocaleString()} tone="action" />
        <MetricCard label="Completed"  value={totals.completed.toLocaleString()} tone="success" />
        <MetricCard label="Errors"     value={totals.errors.toLocaleString()} tone={totals.errors > 0 ? 'danger' : 'success'} />
      </div>
      <Card padding="sm">
        <Card.Header title="Throughput pipeline" subtitle={`Bucket size: ${bucketSize}`} />
        <Card.Body><ThroughputPipelineChart buckets={buckets} /></Card.Body>
      </Card>
      <Card padding="sm">
        <Card.Header title="Latency drift" subtitle="P50 / P90 / P99 over time." />
        <Card.Body><LatencyDriftChart buckets={buckets} /></Card.Body>
      </Card>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════
 *  RUNS SUB-TAB  (RC-39)
 *
 *  Per-run drill-down for both Fleet and per-workflow views. Self-fetches
 *  because its filter / sort / page state is dynamic — different from the
 *  other sub-tabs which fetch once per window. Reuses Table + Pagination
 *  primitives so it matches the rest of the V2 design system.
 *
 *  Props:
 *    workflowId   null = fleet across the tenant, set = scoped to one
 *    fromInstant  ISO string lower bound (parent computes from windowId)
 *    windowLabel  e.g. "Last 1 hour" — shown in the empty / banner states
 *
 *  Click-through:
 *    A row's "Open" button navigates to the canvas run view via
 *    /canvas?workflowId=…&runId=… — the existing surface for run detail.
 * ──────────────────────────────────────────────────────────────────────── */
function RunsSubTab({ workflowId, fromInstant, windowLabel }) {
  const navigate = useNavigate();
  const [status, setStatus]       = useState('');         // '', completed, failed, running, cancelled
  const [minDurMs, setMinDurMs]   = useState(0);          // 0 = no filter
  const [sortBy, setSortBy]       = useState('startedDesc');
  const [page, setPage]           = useState(0);          // 0-indexed
  const pageSize = 25;

  const [data, setData]   = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Reset to page 0 when filters change so the operator isn't stranded
  // on an empty page if the new filter has fewer total results.
  useEffect(() => { setPage(0); }, [status, minDurMs, sortBy, workflowId, fromInstant]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.getWorkflowIntelligenceRuns({
      workflowId: workflowId || undefined,
      from: fromInstant,
      status: status || undefined,
      durationMinMs: minDurMs > 0 ? minDurMs : undefined,
      sortBy,
      limit: pageSize,
      offset: page * pageSize,
    })
      .then(r => { if (!cancelled) setData(r?.data || r || null); })
      .catch(e => { if (!cancelled) setError(e?.message || 'Failed to load runs'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [workflowId, fromInstant, status, minDurMs, sortBy, page]);

  const runs = data?.runs || [];
  const total = data?.totalCount || 0;
  const truncated = !!data?.windowTruncated;

  /* ── Status filter chips ───────────────────────────────────────────── */
  const STATUS_FILTERS = [
    { key: '',          label: 'All' },
    { key: 'completed', label: 'Completed' },
    { key: 'failed',    label: 'Failed' },
    { key: 'running',   label: 'In-flight' },
    { key: 'cancelled', label: 'Cancelled' },
  ];
  const DUR_FILTERS = [
    { key: 0,    label: 'Any' },
    { key: 1000, label: '> 1 s' },
    { key: 5000, label: '> 5 s' },
    { key: 30000, label: '> 30 s' },
  ];

  const fmtTimestamp = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };
  const statusTone = (s) => {
    const v = (s || '').toLowerCase();
    if (v === 'completed') return 'success';
    if (v === 'failed' || v === 'timed_out' || v === 'terminated') return 'danger';
    if (v === 'running' || v === 'pending') return 'action';
    if (v === 'cancelled') return 'warning';
    return 'muted';
  };

  return (
    <div className="space-y-3">
      {/* Filter / sort header */}
      <div className="flex flex-wrap items-center gap-2 text-[11.5px]">
        <span className="text-v2-ink-muted font-medium">Status:</span>
        {STATUS_FILTERS.map(f => (
          <button key={f.key || 'all'}
            type="button"
            onClick={() => setStatus(f.key)}
            className={'px-2 py-0.5 rounded-full border ' +
              (status === f.key
                ? 'bg-v2-action-strong border-v2-action-strong text-white'
                : 'border-v2-border text-v2-ink hover:bg-v2-bg-subtle')}>
            {f.label}
          </button>
        ))}
        <span className="text-v2-ink-muted font-medium ml-2">Duration:</span>
        {DUR_FILTERS.map(f => (
          <button key={f.key}
            type="button"
            onClick={() => setMinDurMs(f.key)}
            className={'px-2 py-0.5 rounded-full border ' +
              (minDurMs === f.key
                ? 'bg-v2-action-strong border-v2-action-strong text-white'
                : 'border-v2-border text-v2-ink hover:bg-v2-bg-subtle')}>
            {f.label}
          </button>
        ))}
        <span className="text-v2-ink-muted font-medium ml-2">Sort:</span>
        <select value={sortBy} onChange={e => setSortBy(e.target.value)}
          className="px-2 py-0.5 rounded border border-v2-border bg-v2-bg text-v2-ink">
          <option value="startedDesc">Newest first</option>
          <option value="startedAsc">Oldest first</option>
          <option value="durationDesc">Slowest first</option>
          <option value="durationAsc">Fastest first</option>
        </select>
        <span className="ml-auto text-v2-ink-muted">
          {loading ? '' : `${total.toLocaleString()} run${total === 1 ? '' : 's'} in ${windowLabel.toLowerCase()}`}
        </span>
      </div>

      {truncated && (
        <div className="text-[11.5px] text-v2-warning-ink bg-v2-warning-bg/40 border border-v2-warning-border rounded px-2 py-1">
          Window contains more runs than the server hard-limit; showing the most recent {data?.runs?.length || 0} of the loaded sample. Narrow the time window for full coverage.
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center gap-2 py-6 text-[12.5px] text-v2-ink-secondary">
          <Loader2 size={14} className="animate-spin text-v2-action-strong" />
          Loading runs…
        </div>
      )}

      {!loading && error && (
        <EmptyState icon={<AlertTriangle size={20} />} title="Couldn't load runs" description={error} />
      )}

      {!loading && !error && runs.length === 0 && (
        <EmptyState icon={<ListIcon size={20} />} title="No runs in window"
          description={`No workflow runs match these filters in ${windowLabel.toLowerCase()}.`} />
      )}

      {!loading && !error && runs.length > 0 && (
        <>
          {/* RC-55 — Use the V2 Table primitive's column-driven API
              (columns + rows props), NOT the JSX-child <thead>/<tbody>
              pattern. The primitive does columns.map(...) inside, which
              blew up with "Cannot read properties of undefined (reading
              'map')" when columns was undefined. Hide the Workflow
              column in single-workflow drill-in mode by conditionally
              including it in the columns array. */}
          <Table
            ariaLabel="Runs"
            density="compact"
            rowKey={(r) => r.runId}
            columns={[
              { key: 'run', header: 'Run', align: 'left', render: (r) => (
                <span className="font-mono text-[11.5px]" title={r.runId}>
                  {String(r.runId || '').slice(0, 8)}…
                </span>
              )},
              ...(workflowId ? [] : [{
                key: 'workflow', header: 'Workflow', align: 'left',
                render: (r) => (
                  <EntityLabel
                    id={r.workflowId}
                    name={r.workflowName}
                    kind="workflow"
                    size="sm"
                  />
                ),
              }]),
              { key: 'status', header: 'Status', align: 'left', render: (r) => (
                <div>
                  <Pill tone={statusTone(r.status)} size="sm" dot>{r.status || 'unknown'}</Pill>
                  {r.errorSnippet && (
                    <div className="text-[10.5px] text-v2-danger-ink mt-0.5 truncate" title={r.errorSnippet}>
                      {r.errorSnippet}
                    </div>
                  )}
                </div>
              )},
              { key: 'started', header: 'Started', align: 'left', render: (r) => (
                <span className="text-[11.5px] text-v2-ink-secondary">{fmtTimestamp(r.startedAt)}</span>
              )},
              { key: 'duration', header: 'Duration', align: 'right', render: (r) => (
                <span className="font-mono text-[11.5px]">{fmtMs(r.durationMs)}</span>
              )},
              { key: 'trigger', header: 'Trigger', align: 'left', render: (r) => (
                <span className="text-[11.5px] text-v2-ink-secondary">{r.triggerType || '—'}</span>
              )},
              { key: 'open', header: '', align: 'right', render: (r) => (
                <button
                  type="button"
                  className="text-[11.5px] text-v2-action-strong hover:underline inline-flex items-center gap-1"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/canvas?workflowId=${r.workflowId}&runId=${r.runId}`);
                  }}>
                  Open <ArrowRight size={11} />
                </button>
              )},
            ]}
            rows={runs}
          />

          {/* Pagination — server-side. Total drives whether next/prev are enabled. */}
          {total > pageSize && (
            <div className="flex items-center justify-between text-[11.5px] text-v2-ink-secondary pt-1">
              <span>
                Showing {page * pageSize + 1}–{Math.min(total, (page + 1) * pageSize)} of {total.toLocaleString()}
              </span>
              <div className="flex items-center gap-1">
                <Button size="sm" variant="ghost"
                  disabled={page === 0}
                  onClick={() => setPage(p => Math.max(0, p - 1))}>Prev</Button>
                <Button size="sm" variant="ghost"
                  disabled={!data?.hasMore}
                  onClick={() => setPage(p => p + 1)}>Next</Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function GovernanceSubTab({ data }) {
  const stages = data?.stages || data?.items || data?.rollup || [];
  if (!Array.isArray(stages) || stages.length === 0) return <EmptyState icon={<Shield size={20} />}
    title="No protection evaluations" description="No stages fired in this window for this workflow." />;
  const totals = stages.reduce((acc, s) => ({
    allowed: acc.allowed + Number(s.allowed ?? s.allow ?? 0),
    warned:  acc.warned  + Number(s.warned ?? s.warn ?? 0),
    blocked: acc.blocked + Number(s.blocked ?? s.block ?? 0),
  }), { allowed: 0, warned: 0, blocked: 0 });
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        <MetricCard label="Allowed" value={totals.allowed.toLocaleString()} tone="success" />
        <MetricCard label="Warned"  value={totals.warned.toLocaleString()} tone={totals.warned > 0 ? 'warning' : 'success'} />
        <MetricCard label="Blocked" value={totals.blocked.toLocaleString()} tone={totals.blocked > 0 ? 'danger' : 'success'} />
      </div>
      <Card padding="sm">
        <Card.Header title="Stage decisions" />
        <Card.Body><StageDecisionStackedBar stages={stages} /></Card.Body>
      </Card>
      <Card padding="sm">
        <Card.Header title="Per-stage rollup" />
        <Card.Body>
          <Table
            ariaLabel="Stage rollup"
            density="compact"
            columns={[
              { key: 'stage', header: 'Stage', render: (r) => <span className="font-semibold text-[11.5px]">{r.stage || r.name}</span> },
              { key: 'fired', header: 'Fired', align: 'right', render: (r) => <span className="tabular-nums text-[11.5px]">{(r.fired ?? r.count ?? 0).toLocaleString()}</span> },
              { key: 'allow', header: 'Allow', align: 'right', render: (r) => <span className="tabular-nums text-[11.5px] text-v2-success-strong">{(r.allowed ?? r.allow ?? 0).toLocaleString()}</span> },
              { key: 'warn',  header: 'Warn',  align: 'right', render: (r) => <span className="tabular-nums text-[11.5px] text-v2-warning-strong">{(r.warned ?? r.warn ?? 0).toLocaleString()}</span> },
              { key: 'block', header: 'Block', align: 'right', render: (r) => (
                <span className={'tabular-nums text-[11.5px] ' + ((r.blocked ?? r.block ?? 0) > 0 ? 'text-v2-danger-strong font-semibold' : 'text-v2-ink-muted')}>
                  {(r.blocked ?? r.block ?? 0).toLocaleString()}
                </span>
              )},
            ]}
            rows={stages}
            rowKey={(r, i) => r.stage || r.name || i}
          />
        </Card.Body>
      </Card>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════
 *  TOP-LEVEL SLIDEOUTS — Benchmarks + AI Analyst
 *
 *  G356 — Converted from Modal to Slideout to match the V2 pattern used
 *  across Trust Center, Compliance, and Audit Trail. Modals don't fit
 *  the V2 information architecture: slideouts give the operator a
 *  persistent edge anchor so the page underneath stays in context.
 *
 *  Both operate ACROSS workflows so they belong at fleet level, not
 *  inside the per-workflow slideout.
 * ──────────────────────────────────────────────────────────────────────── */
function BenchmarksSlideout({ isOpen, onClose, benchmarks }) {
  const [compareSet, setCompareSet] = useState(new Set());
  const [detail, setDetail] = useState(null);
  const [compareOpen, setCompareOpen] = useState(false);

  const toggleCompare = (id) => setCompareSet(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id);
    else if (next.size < 6) next.add(id);
    return next;
  });
  const selectedForCompare = benchmarks.filter(b => compareSet.has(b.id));

  return (
    <Slideout
      isOpen={isOpen}
      onClose={onClose}
      width={920}
      ariaLabel="Benchmarks"
      header={
        <div className="flex items-center gap-2">
          <BarChart3 size={16} className="text-v2-action-strong" />
          <h2 className="text-[16px] font-bold m-0">Benchmarks</h2>
          <Pill tone="muted" size="sm">{benchmarks.length}</Pill>
        </div>
      }
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Close</Button>
          <Button variant="primary" disabled={compareSet.size < 2}
            iconLeft={<Copy size={13} />}
            onClick={() => setCompareOpen(true)}>
            Compare {compareSet.size}
          </Button>
        </>
      }
    >
      <Table
        ariaLabel="Benchmarks"
        density="compact"
        columns={[
          { key: 'select', header: '', render: (r) => (
            <input type="checkbox" checked={compareSet.has(r.id)}
              onChange={(e) => { e.stopPropagation(); toggleCompare(r.id); }}
              onClick={(e) => e.stopPropagation()}
              aria-label={`Select ${r.name || r.id}`} />
          )},
          { key: 'name', header: 'Name', render: (r) => (
            <div>
              <div className="font-semibold text-[12.5px] text-v2-ink">{r.name || r.id}</div>
              <div className="text-[10.5px] text-v2-ink-muted">{r.kind || r.type || 'benchmark'} · {r.environment || 'env'}</div>
            </div>
          )},
          { key: 'status', header: 'Status', render: (r) => {
            const s = (r.status || '').toUpperCase();
            return s === 'COMPLETED' ? <Pill tone="success" size="sm">COMPLETED</Pill>
                 : s === 'RUNNING'   ? <Pill tone="action"  size="sm" dot>RUNNING</Pill>
                 : s === 'FAILED'    ? <Pill tone="danger"  size="sm">FAILED</Pill>
                 : s === 'ABORTED'   ? <Pill tone="warning" size="sm">ABORTED</Pill>
                                     : <Pill tone="muted"   size="sm">{s || '—'}</Pill>;
          }},
          { key: 'started', header: 'Started', render: (r) => (
            <span className="text-[11px] text-v2-ink-muted">{fmtTime(r.startedAt)}</span>
          )},
          { key: 'fired', header: 'Fired', align: 'right', render: (r) => (
            <span className="tabular-nums text-[11px]">{(r.firedCount ?? r.fired ?? 0).toLocaleString()}</span>
          )},
          { key: 'errors', header: 'Errors', align: 'right', render: (r) => {
            const e = r.errorCount ?? r.errors ?? 0;
            return <span className={'tabular-nums text-[11px] ' + (e > 0 ? 'text-v2-danger font-semibold' : 'text-v2-ink-muted')}>
              {e.toLocaleString()}
            </span>;
          }},
          { key: 'open', header: '', align: 'right', render: (r) => (
            <Button variant="ghost" size="xs" iconRight={<ArrowRight size={11} />}
              onClick={(e) => { e.stopPropagation(); setDetail(r); }}>
              Detail
            </Button>
          )},
        ]}
        rows={benchmarks}
        rowKey={(r) => r.id}
        onRowClick={setDetail}
      />

      <BenchmarkDetailSlideout run={detail} onClose={() => setDetail(null)}
        onAddToCompare={(r) => { toggleCompare(r.id); }} />
      <BenchmarkCompareSlideout
        isOpen={compareOpen}
        runs={selectedForCompare}
        onClose={() => setCompareOpen(false)} />
    </Slideout>
  );
}

function AIAnalystSlideout({ isOpen, onClose, workflows, benchmarks, defaultWorkflowId, windowId }) {
  const [scopeWorkflowId, setScopeWorkflowId] = useState(defaultWorkflowId || '');
  /* G356 — Run scope. AI Analyst can now analyze:
   *   1. Fleet (no workflowId, no runId)
   *   2. A specific workflow across the window
   *   3. A specific run (workflowId + runId — narrowest scope)
   * Run picker only shows when a workflow is chosen, since runs are
   * always scoped under a workflow. */
  const [scopeRunId, setScopeRunId] = useState('');
  const [aiData, setAiData] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);
  useEffect(() => { setScopeWorkflowId(defaultWorkflowId || ''); setScopeRunId(''); }, [defaultWorkflowId]);
  useEffect(() => { if (!isOpen) { setAiData(null); setAiError(null); } }, [isOpen]);

  const runAnalyze = async ({ force = false } = {}) => {
    setAiLoading(true); setAiError(null);
    try {
      const hours = hoursForWindow(windowId);
      const fromInstant = new Date(Date.now() - hours * 3600 * 1000).toISOString();
      const res = await api.getWorkflowIntelligenceAIAnalysis?.({
        from: fromInstant,
        workflowId: scopeWorkflowId || null,
        runId:      scopeRunId || null,
        force,
      });
      setAiData(res?.data || res || null);
    } catch (e) { setAiError(e?.message || 'Analysis failed'); }
    finally { setAiLoading(false); }
  };

  const posture = (aiData?.posture || aiData?.overallPosture || 'unknown').toUpperCase();
  const postureTone = posture === 'HEALTHY' ? 'success'
                    : posture === 'DEGRADED' ? 'warning'
                    : posture === 'CRITICAL' ? 'danger' : 'muted';
  const findings = aiData?.findings || aiData?.issues || [];
  const recs = aiData?.recommendations || aiData?.actions || [];

  /* G356 — Filter the run picker to runs of the selected workflow.
   * Benchmarks list carries workflowId per run, so we scope to that. */
  const runOptionsForWorkflow = useMemo(() => {
    if (!scopeWorkflowId || !Array.isArray(benchmarks)) return [];
    return benchmarks.filter(b =>
      (b.workflowId === scopeWorkflowId) ||
      (Array.isArray(b.workflowIds) && b.workflowIds.includes(scopeWorkflowId))
    );
  }, [scopeWorkflowId, benchmarks]);

  return (
    <Slideout
      isOpen={isOpen}
      onClose={onClose}
      width={640}
      ariaLabel="AI Analyst"
      header={
        <div className="flex items-center gap-2">
          <Brain size={16} className="text-v2-action-strong" />
          <h2 className="text-[16px] font-bold m-0">AI Analyst</h2>
          <span className="text-[11px] text-v2-ink-muted">· {WINDOW_OPTIONS.find(o => o.id === windowId)?.label}</span>
        </div>
      }
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Close</Button>
          <Button variant="primary" iconLeft={aiLoading ? <Loader2 size={13} className="animate-spin" /> : <Brain size={13} />}
            disabled={aiLoading}
            onClick={() => runAnalyze({ force: aiData != null })}>
            {aiLoading ? 'Analyzing…' : aiData ? 'Re-analyze' : 'Analyze'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="rounded-v2-md bg-v2-muted p-3 space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <label className="text-[10.5px] uppercase tracking-wider text-v2-ink-muted font-semibold w-16">Workflow</label>
            <select value={scopeWorkflowId} onChange={(e) => { setAiData(null); setScopeWorkflowId(e.target.value); setScopeRunId(''); }}
              className="flex-1 text-[12px] border border-v2-border-subtle rounded-v2-sm px-2 py-1 bg-v2-surface focus:border-v2-action focus:outline-none">
              <option value="">All workflows (fleet aggregate)</option>
              {workflows.map(w => (
                <option key={w.id} value={w.id}>{(w.name || w.workflowName || w.id).slice(0, 70)}</option>
              ))}
            </select>
          </div>
          {scopeWorkflowId && (
            <div className="flex items-center gap-2 flex-wrap">
              <label className="text-[10.5px] uppercase tracking-wider text-v2-ink-muted font-semibold w-16">Run</label>
              <select value={scopeRunId} onChange={(e) => { setAiData(null); setScopeRunId(e.target.value); }}
                className="flex-1 text-[12px] border border-v2-border-subtle rounded-v2-sm px-2 py-1 bg-v2-surface focus:border-v2-action focus:outline-none">
                <option value="">All runs in window (aggregate)</option>
                {runOptionsForWorkflow.length === 0 && (
                  <option value="" disabled>No recorded benchmark runs for this workflow</option>
                )}
                {runOptionsForWorkflow.slice(0, 30).map(r => (
                  <option key={r.id} value={r.id}>
                    {(r.name || r.id).slice(0, 50)}{r.completedAt ? '' : ' · running'}
                  </option>
                ))}
              </select>
            </div>
          )}
          <p className="text-[10.5px] text-v2-ink-muted m-0">
            {scopeRunId ? 'Run-level analysis — deepest scope.'
              : scopeWorkflowId ? 'Workflow analysis — aggregates all runs in the time window.'
              : 'Fleet analysis — aggregates every workflow in the tenant.'}
          </p>
        </div>

        {!aiData && !aiLoading && (
          <div className="text-center py-8">
            <Brain size={28} className="text-v2-action mx-auto mb-2" />
            <p className="text-[12.5px] text-v2-ink-secondary m-0 max-w-md mx-auto">
              Click <strong>Analyze</strong> to run Claude over the selected scope. Returns severity-ranked findings + prescriptive Spring property tuning.
            </p>
            {aiError && <p className="text-[12px] text-v2-danger mt-3">{aiError}</p>}
          </div>
        )}

        {aiLoading && (
          <div className="flex items-center justify-center gap-3 py-10 text-[13px] text-v2-ink-secondary">
            <Loader2 size={18} className="animate-spin text-v2-action-strong" />
            Analyzing — may take up to 90 seconds…
          </div>
        )}

        {aiData && (
          <>
            <Card padding="sm">
              <Card.Body>
                <div className="flex items-center gap-2 mb-2">
                  <Brain size={14} className="text-v2-action-strong" />
                  <strong className="text-[13.5px]">Summary</strong>
                  <Pill tone={postureTone} size="sm" dot>{posture}</Pill>
                </div>
                <p className="text-[12.5px] text-v2-ink-secondary leading-relaxed m-0">
                  {aiData.summary || aiData.tldr || 'No summary returned.'}
                </p>
              </Card.Body>
            </Card>

            <Card padding="sm">
              <Card.Header title={`Findings · ${findings.length}`} />
              <Card.Body>
                {findings.length === 0
                  ? <p className="text-[12.5px] text-v2-ink-muted m-0 italic">No findings.</p>
                  : (
                    <ul className="space-y-2 m-0 p-0 list-none">
                      {findings.map((f, i) => {
                        const sev = (f.severity || 'info').toUpperCase();
                        const tone = sev === 'CRITICAL' || sev === 'HIGH' ? 'danger'
                                   : sev === 'MEDIUM' ? 'warning' : 'cyan';
                        return (
                          <li key={i} className="rounded-v2-sm border border-v2-border-subtle p-3 bg-v2-surface">
                            <div className="flex items-center gap-2 mb-1.5">
                              <Pill tone={tone} size="sm">{sev}</Pill>
                              <strong className="text-[12.5px] text-v2-ink">{f.title || `Finding ${i + 1}`}</strong>
                            </div>
                            <p className="text-[12px] text-v2-ink-secondary m-0">{f.description || f.detail}</p>
                          </li>
                        );
                      })}
                    </ul>
                  )}
              </Card.Body>
            </Card>

            <Card padding="sm">
              <Card.Header title={`Recommendations · ${recs.length}`} />
              <Card.Body>
                {recs.length === 0
                  ? <p className="text-[12.5px] text-v2-ink-muted m-0 italic">No recommendations.</p>
                  : (
                    <ul className="space-y-2 m-0 p-0 list-none">
                      {recs.map((r, i) => (
                        <li key={i} className="rounded-v2-sm border border-v2-border-subtle p-3 bg-v2-surface">
                          <div className="flex items-center gap-2 mb-1">
                            <CheckCircle size={13} className="text-v2-success" />
                            <strong className="text-[12.5px] text-v2-ink">{r.title || r.action || `Recommendation ${i + 1}`}</strong>
                          </div>
                          <p className="text-[12px] text-v2-ink-secondary m-0">{r.description || r.rationale}</p>
                          {r.property && (
                            <code className="inline-block mt-2 text-[10.5px] font-mono-v2 bg-v2-muted rounded-v2-sm px-2 py-1">
                              {r.property}{r.value != null ? ` = ${r.value}` : ''}
                            </code>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
              </Card.Body>
            </Card>
          </>
        )}
      </div>
    </Slideout>
  );
}

function BenchmarkDetailSlideout({ run, onClose, onAddToCompare }) {
  if (!run) return null;
  const dur = run.completedAt && run.startedAt
    ? new Date(run.completedAt).getTime() - new Date(run.startedAt).getTime() : null;
  return (
    <Slideout
      isOpen={!!run}
      onClose={onClose}
      width={620}
      ariaLabel={`Benchmark ${run.name || run.id} detail`}
      header={
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Activity size={14} className="text-v2-action-strong" />
            <Pill tone={
              run.status === 'COMPLETED' ? 'success'
              : run.status === 'RUNNING' ? 'action'
              : run.status === 'FAILED'  ? 'danger' : 'muted'} size="sm" dot>
              {run.status || 'unknown'}
            </Pill>
            <span className="text-[11.5px] text-v2-ink-muted">{run.kind || run.type || 'benchmark'}</span>
          </div>
          <h2 className="text-[16px] font-bold m-0">{run.name || run.id}</h2>
          <p className="text-[11px] text-v2-ink-muted mt-0.5">{run.environment || 'unknown env'} · {fmtTime(run.startedAt)}</p>
        </div>
      }
      footer={
        <div className="flex gap-2">
          <Button variant="secondary" size="sm" iconLeft={<Copy size={13} />} onClick={() => onAddToCompare(run)}>
            Add to compare
          </Button>
          <Button variant="secondary" size="sm" iconLeft={<Download size={13} />}
            onClick={() => api.exportBenchmark?.(run.id, 'pdf')?.catch(() => {})}>
            Export PDF
          </Button>
          <Button variant="secondary" size="sm" iconLeft={<Download size={13} />}
            onClick={() => api.exportBenchmark?.(run.id, 'xlsx')?.catch(() => {})}>
            Export XLSX
          </Button>
        </div>
      }
    >
      <Card padding="sm">
        <Card.Header title="Overview" />
        <Card.Body>
          <dl className="grid grid-cols-2 gap-3 text-[12.5px]">
            <Stat label="Started"   value={fmtTime(run.startedAt)} />
            <Stat label="Completed" value={fmtTime(run.completedAt) || 'running'} />
            <Stat label="Duration"  value={dur != null ? fmtMs(dur) : '—'} />
            <Stat label="Fired"     value={(run.firedCount ?? run.fired ?? 0).toLocaleString()} />
            <Stat label="Errors"    value={(run.errorCount ?? run.errors ?? 0).toLocaleString()} />
            <Stat label="Workflow"  value={run.workflowName || run.workflowId || '—'} />
          </dl>
        </Card.Body>
      </Card>
      {run.serverMetrics && (
        <Card padding="sm" className="mt-3">
          <Card.Header title="Server metrics" />
          <Card.Body>
            <pre className="text-[10.5px] font-mono-v2 bg-v2-muted rounded-v2-sm p-3 overflow-auto max-h-[240px] m-0">
              {JSON.stringify(run.serverMetrics, null, 2)}
            </pre>
          </Card.Body>
        </Card>
      )}
    </Slideout>
  );
}

function BenchmarkCompareSlideout({ isOpen, runs, onClose }) {
  if (!runs || runs.length === 0) return null;
  const baseline = runs[0];
  return (
    <Slideout
      isOpen={isOpen}
      onClose={onClose}
      width={780}
      ariaLabel="Benchmark comparison"
      header={
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Activity size={14} className="text-v2-action-strong" />
            <Pill tone="action" size="sm">Comparing {runs.length}</Pill>
          </div>
          <h2 className="text-[16px] font-bold m-0">Benchmark compare</h2>
          <p className="text-[11.5px] text-v2-ink-muted mt-0.5">Baseline: <strong>{baseline.name || baseline.id}</strong></p>
        </div>
      }
    >
      <Card padding="sm">
        <Card.Body>
          <Table
            ariaLabel="Benchmark comparison"
            density="compact"
            columns={[
              { key: 'name', header: 'Run', render: (r) => (
                <div>
                  <div className="font-semibold text-[12px]">{r.name || r.id}</div>
                  <div className="text-[10.5px] text-v2-ink-muted">{r.environment || 'env'}</div>
                </div>
              )},
              { key: 'started', header: 'Started', render: (r) => (
                <span className="text-[11px] text-v2-ink-muted">{fmtTime(r.startedAt)}</span>
              )},
              { key: 'fired', header: 'Fired', align: 'right', render: (r, i) => {
                const v = r.firedCount ?? r.fired ?? 0;
                const baseV = baseline.firedCount ?? baseline.fired ?? 0;
                const delta = i === 0 ? null : v - baseV;
                return (
                  <span className="font-mono-v2 text-[11.5px]">
                    {v.toLocaleString()}
                    {delta != null && delta !== 0 && (
                      <span className={'ml-1 text-[10px] ' + (delta > 0 ? 'text-v2-success-strong' : 'text-v2-danger-strong')}>
                        {delta > 0 ? '+' : ''}{delta.toLocaleString()}
                      </span>
                    )}
                  </span>
                );
              }},
              { key: 'errors', header: 'Errors', align: 'right', render: (r, i) => {
                const v = r.errorCount ?? r.errors ?? 0;
                const baseV = baseline.errorCount ?? baseline.errors ?? 0;
                const delta = i === 0 ? null : v - baseV;
                return (
                  <span className={'font-mono-v2 text-[11.5px] ' + (v > 0 ? 'text-v2-danger' : 'text-v2-ink-muted')}>
                    {v.toLocaleString()}
                    {delta != null && delta !== 0 && (
                      <span className={'ml-1 text-[10px] ' + (delta < 0 ? 'text-v2-success-strong' : 'text-v2-danger-strong')}>
                        {delta > 0 ? '+' : ''}{delta.toLocaleString()}
                      </span>
                    )}
                  </span>
                );
              }},
            ]}
            rows={runs}
            rowKey={(r) => r.id}
          />
        </Card.Body>
      </Card>
    </Slideout>
  );
}

/* ════════════════════════════════════════════════════════════════════════
 *  CHART PRIMITIVES (inline SVG; no Recharts dependency)
 * ──────────────────────────────────────────────────────────────────────── */
/* G356 — Latency-range fallback labels.
 *
 * Backend's `distribution` array is expected to carry one of:
 *   { label: "10–50 ms", count: N }                        (explicit)
 *   { lowerMs: 10, upperMs: 50, count: N }                 (numeric bounds)
 *   { count: N }   (no bounds — fall back to this table)
 *
 * The previous fallback was `bucket ${i}`, which is meaningless to
 * operators looking at the chart. These ranges mirror what V1's
 * Performance histogram uses: 12 log-spaced buckets covering sub-10ms
 * to over-30s. The 13th catch-all sits beyond the 12-bucket array;
 * any extra-wide histogram drops back to "tier N" so we don't claim
 * a range we can't justify. */
const LATENCY_BUCKETS_FALLBACK = [
  '< 10 ms',
  '10 – 25 ms',
  '25 – 50 ms',
  '50 – 100 ms',
  '100 – 250 ms',
  '250 – 500 ms',
  '500 ms – 1 s',
  '1 – 2.5 s',
  '2.5 – 5 s',
  '5 – 10 s',
  '10 – 30 s',
  '> 30 s',
];

function bucketLabel(b, i, total) {
  if (b.label) return b.label;
  if (b.bucket && !/^bucket\s*\d/i.test(b.bucket)) return b.bucket;
  if (b.lowerMs != null && b.upperMs != null) return `${fmtMs(b.lowerMs)} – ${fmtMs(b.upperMs)}`;
  if (total === LATENCY_BUCKETS_FALLBACK.length && i < LATENCY_BUCKETS_FALLBACK.length) {
    return LATENCY_BUCKETS_FALLBACK[i];
  }
  return `tier ${i + 1}`;
}

function Histogram({ buckets }) {
  const maxCount = Math.max(1, ...buckets.map(b => Number(b.count ?? b.runs ?? 0)));
  const W = 760, H = 200, padL = 40, padR = 8, padT = 8, padB = 70;
  const innerW = W - padL - padR, innerH = H - padT - padB;
  const barW = innerW / buckets.length;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Latency distribution" className="w-full">
      {/* Y-axis title */}
      <text x={12} y={padT + innerH / 2} fontSize={9} fill="var(--v2-ink-muted)" textAnchor="middle"
            transform={`rotate(-90 12 ${padT + innerH / 2})`}>
        runs
      </text>
      {[0, 0.5, 1].map((t, i) => (
        <g key={i}>
          <line x1={padL} x2={padL + innerW} y1={padT + innerH * (1 - t)} y2={padT + innerH * (1 - t)} stroke="var(--v2-border-subtle)" strokeWidth={0.5} />
          <text x={padL - 4} y={padT + innerH * (1 - t) + 3} fontSize={9} fill="var(--v2-ink-muted)" textAnchor="end">
            {Math.round(maxCount * t).toLocaleString()}
          </text>
        </g>
      ))}
      {buckets.map((b, i) => {
        const v = Number(b.count ?? b.runs ?? 0);
        const h = (v / maxCount) * innerH;
        const x = padL + i * barW + 1;
        const y = padT + innerH - h;
        const hue = 200 - (180 * (i / Math.max(1, buckets.length - 1)));
        const label = bucketLabel(b, i, buckets.length);
        return (
          <g key={i}>
            <rect x={x} y={y} width={Math.max(1, barW - 2)} height={h} fill={`hsl(${hue}, 70%, 55%)`} rx={2}>
              <title>{label}: {v.toLocaleString()} runs</title>
            </rect>
            <text x={x + barW / 2} y={padT + innerH + 12} fontSize={8.5} fill="var(--v2-ink-muted)" textAnchor="end"
                  transform={`rotate(-35 ${x + barW / 2} ${padT + innerH + 12})`}>
              {label}
            </text>
          </g>
        );
      })}
      {/* X-axis title */}
      <text x={padL + innerW / 2} y={H - 4} fontSize={9} fill="var(--v2-ink-muted)" textAnchor="middle">
        run duration
      </text>
    </svg>
  );
}

function PercentileBars({ workflow, task }) {
  const pcts = ['p50', 'p90', 'p95', 'p99', 'max'];
  const maxV = Math.max(1, ...pcts.flatMap(p => [Number(workflow[p] || 0), Number(task[p] || 0)]));
  return (
    <div className="space-y-2 text-[12px]">
      {pcts.map(p => {
        const wf = Number(workflow[p] || 0);
        const tk = Number(task[p] || 0);
        const wfPct = maxV ? (wf / maxV) * 100 : 0;
        const tkPct = maxV ? (tk / maxV) * 100 : 0;
        return (
          <div key={p}>
            <div className="flex items-baseline justify-between mb-1">
              <span className="font-semibold uppercase tracking-wider text-[10.5px]">{p}</span>
              <span className="font-mono-v2">{fmtMs(wf)} <span className="text-v2-ink-muted">·</span> <span className="text-v2-ink-muted">task</span> {fmtMs(tk)}</span>
            </div>
            <div className="h-2.5 bg-v2-muted rounded-v2-pill overflow-hidden relative">
              <div className="absolute inset-y-0 left-0 bg-v2-action" style={{ width: `${wfPct}%` }} />
              {tk > 0 && <div className="absolute inset-y-0 left-0 bg-v2-cyan opacity-60" style={{ width: `${tkPct}%` }} />}
            </div>
          </div>
        );
      })}
      <div className="flex items-center gap-3 text-[10px] text-v2-ink-muted pt-1">
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 bg-v2-action rounded-sm" /> workflow</span>
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 bg-v2-cyan rounded-sm opacity-60" /> task</span>
      </div>
    </div>
  );
}

function ThroughputPipelineChart({ buckets }) {
  const W = 760, H = 200, padL = 40, padR = 8, padT = 8, padB = 26;
  const innerW = W - padL - padR, innerH = H - padT - padB;
  const stepX = buckets.length > 1 ? innerW / (buckets.length - 1) : 0;
  const seriesDef = [
    { key: 'dispatched', alts: ['workflowsDispatched'], label: 'Dispatched', color: 'var(--v2-action)' },
    { key: 'completed',  alts: ['workflowsCompleted', 'runs', 'count'], label: 'Completed', color: 'var(--v2-success)' },
    { key: 'errors',     alts: ['failedCount'], label: 'Errors', color: 'var(--v2-danger)' },
  ];
  const extract = (b, def) => {
    for (const k of [def.key, ...def.alts]) if (b[k] != null) return Number(b[k]);
    return 0;
  };
  const maxY = Math.max(1, ...buckets.flatMap(b => seriesDef.map(s => extract(b, s))));
  return (
    <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Throughput pipeline" className="w-full">
      {[0, 0.5, 1].map((t, i) => (
        <line key={i} x1={padL} x2={padL + innerW} y1={padT + innerH * (1 - t)} y2={padT + innerH * (1 - t)} stroke="var(--v2-border-subtle)" strokeWidth={0.5} />
      ))}
      {seriesDef.map(s => {
        const pts = buckets.map((b, i) => {
          const v = extract(b, s);
          const x = padL + i * stepX;
          const y = padT + innerH - (v / maxY) * innerH;
          return `${x.toFixed(1)},${y.toFixed(1)}`;
        }).join(' ');
        return <polyline key={s.key} fill="none" stroke={s.color} strokeWidth={1.6} points={pts} />;
      })}
      {seriesDef.map((s, i) => (
        <g key={s.key} transform={`translate(${padL + i * 90}, ${H - 6})`}>
          <rect width={10} height={3} fill={s.color} />
          <text x={14} y={3} fontSize={9.5} fill="var(--v2-ink-muted)">{s.label}</text>
        </g>
      ))}
    </svg>
  );
}

function LatencyDriftChart({ buckets }) {
  const W = 760, H = 180, padL = 40, padR = 8, padT = 8, padB = 22;
  const innerW = W - padL - padR, innerH = H - padT - padB;
  const stepX = buckets.length > 1 ? innerW / (buckets.length - 1) : 0;
  const lines = [
    { key: 'p50', color: 'var(--v2-success)' },
    { key: 'p90', color: 'var(--v2-warning)' },
    { key: 'p99', color: 'var(--v2-danger)' },
  ];
  const maxY = Math.max(1, ...buckets.flatMap(b => lines.map(l => Number(b[l.key] || 0))));
  return (
    <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Latency drift" className="w-full">
      {[0, 0.5, 1].map((t, i) => (
        <line key={i} x1={padL} x2={padL + innerW} y1={padT + innerH * (1 - t)} y2={padT + innerH * (1 - t)} stroke="var(--v2-border-subtle)" strokeWidth={0.5} />
      ))}
      {lines.map(l => {
        const pts = buckets.map((b, i) => {
          const v = Number(b[l.key] || 0);
          const x = padL + i * stepX;
          const y = padT + innerH - (v / maxY) * innerH;
          return `${x.toFixed(1)},${y.toFixed(1)}`;
        }).join(' ');
        return <polyline key={l.key} fill="none" stroke={l.color} strokeWidth={1.4} points={pts} />;
      })}
      {lines.map((l, i) => (
        <g key={l.key} transform={`translate(${padL + i * 50}, ${H - 4})`}>
          <rect width={9} height={3} fill={l.color} />
          <text x={12} y={3} fontSize={9} fill="var(--v2-ink-muted)">{l.key.toUpperCase()}</text>
        </g>
      ))}
    </svg>
  );
}

function StageDecisionStackedBar({ stages }) {
  const maxFired = Math.max(1, ...stages.map(s => Number(s.fired ?? s.count ?? 0)));
  return (
    <div className="space-y-2">
      {stages.map((s, i) => {
        const fired = Number(s.fired ?? s.count ?? 0);
        const allowed = Number(s.allowed ?? s.allow ?? 0);
        const warned = Number(s.warned ?? s.warn ?? 0);
        const blocked = Number(s.blocked ?? s.block ?? 0);
        const total = allowed + warned + blocked || 1;
        const widthPct = (fired / maxFired) * 100;
        return (
          <div key={s.stage || s.name || i}>
            <div className="flex items-baseline justify-between mb-1 text-[11.5px]">
              <span className="font-semibold">{s.stage || s.name || '—'}</span>
              <span className="font-mono-v2 text-v2-ink-muted">{fired.toLocaleString()} fired</span>
            </div>
            <div className="h-3.5 rounded-v2-sm overflow-hidden flex bg-v2-muted" style={{ width: `${widthPct}%`, minWidth: '4px' }}>
              <div className="bg-v2-success h-full" style={{ width: `${(allowed / total) * 100}%` }} title={`Allow ${allowed}`} />
              <div className="bg-v2-warning h-full" style={{ width: `${(warned  / total) * 100}%` }} title={`Warn ${warned}`} />
              <div className="bg-v2-danger  h-full" style={{ width: `${(blocked / total) * 100}%` }} title={`Block ${blocked}`} />
            </div>
          </div>
        );
      })}
      <div className="flex items-center gap-3 text-[10.5px] text-v2-ink-muted pt-1">
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 bg-v2-success rounded-sm" /> allow</span>
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 bg-v2-warning rounded-sm" /> warn</span>
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 bg-v2-danger rounded-sm" /> block</span>
      </div>
    </div>
  );
}

/* ─── Small helpers ─────────────────────────────────────────────────── */
function Stat({ label, value }) {
  return (
    <div>
      <dt className="text-[10.5px] uppercase tracking-wider text-v2-ink-muted font-semibold">{label}</dt>
      <dd className="text-v2-ink font-mono-v2 mt-0.5 m-0">{value}</dd>
    </div>
  );
}

/* ─── Default export ────────────────────────────────────────────────── */
export default function ResolvedWorkflowIntelligencePage(props) {
  const url = typeof window !== 'undefined' ? new URL(window.location.href) : null;
  const legacy = url?.searchParams?.get('legacy') === '1';
  if (legacy) return <WorkflowIntelligencePage {...props} />;
  return <WorkflowIntelligencePageV2Inner {...props} />;
}

export { WorkflowIntelligencePageV2Inner };
